import React, { useEffect, useRef } from "react";
import { Mic, Play, Pause, StopCircle, X } from "lucide-react";
import logoImage from "../assets/logo.svg";

interface RecordingModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  patientName: string;
  permissionError: boolean;
  isSafari: boolean;
  isChrome: boolean;
  timer: number;
  frequencyData: Uint8Array;
  isRecording: boolean;
  isPaused: boolean;
  isSending: boolean;
  isPlaying: boolean;
  audioBlob: Blob | null;
  formatTime: (seconds: number) => string;
  retryRecording: () => void;
  handleNewRecording: () => void;
  handlePauseResume: () => void;
  stopRecording: () => void;
  handlePlayback: () => void;
  processAudio: (blob: Blob) => void;
}

const RecordingModal: React.FC<RecordingModalProps> = ({
  isModalOpen,
  permissionError,
  isSafari,
  isChrome,
  timer,
  frequencyData,
  isRecording,
  isPaused,
  isSending,
  isPlaying,
  audioBlob,
  formatTime,
  retryRecording,
  handleNewRecording,
  handlePauseResume,
  stopRecording,
  handlePlayback,
  processAudio,
}) => {
  const animationFrameRef = useRef<number>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const waveformDataRef = useRef<number[]>([]);
  const offsetRef = useRef(0);
  const historyRef = useRef<number[][]>([]);
  const hasProcessedRef = useRef(false);

  // Initialize waveform data
  useEffect(() => {
    waveformDataRef.current = Array(128).fill(0);
    historyRef.current = Array(50).fill(Array(128).fill(0));
  }, []);

  // Reset hasProcessed when modal is closed
  useEffect(() => {
    if (!isModalOpen) {
      hasProcessedRef.current = false;
    }
  }, [isModalOpen]);

  useEffect(() => {
    // Auto-process when audio blob is available and not recording
    if (
      audioBlob &&
      !isRecording &&
      !isSending &&
      !isPlaying &&
      !hasProcessedRef.current
    ) {
      hasProcessedRef.current = true;
      processAudio(audioBlob);
    }
  }, [audioBlob, isRecording, isSending, isPlaying, processAudio]);

  // Canvas animation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size with device pixel ratio for retina displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    let lastUpdate = Date.now();

    const animate = () => {
      const now = Date.now();
      const deltaTime = now - lastUpdate;

      if (deltaTime > 16) {
        lastUpdate = now;

        // Only update offset during recording
        if (isRecording && !isPaused) {
          offsetRef.current = (offsetRef.current + 1) % rect.width;
        }

        // Clear canvas with a clean white background
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, rect.width, rect.height);

        if (isRecording && !isPaused) {
          // Process frequency data
          const processedData = Array.from(frequencyData)
            .slice(0, 64)
            .map((value, index) => {
              const weight = index < 32 ? 1.5 : 1.0;
              return Math.min((value / 128.0) * weight, 1.0);
            });

          // Update history with smoothing
          const newData = processedData.map((value, i) => {
            const prevValue =
              historyRef.current[historyRef.current.length - 1]?.[i] || 0;
            return prevValue * 0.3 + value * 0.7;
          });

          historyRef.current.push(newData);
          historyRef.current = historyRef.current.slice(-30);

          // Draw center wave line based on frequency
          ctx.beginPath();
          ctx.strokeStyle = "rgba(10, 132, 255, 0.6)";
          ctx.lineWidth = 3;

          // Create points for the wave
          const points = Array.from(frequencyData)
            .slice(0, 32)
            .map((value, index) => ({
              x: (index * rect.width) / 31,
              y: rect.height / 2 + (value / 256) * 50 * Math.sin(index / 2),
            }));

          // Draw smooth wave
          ctx.moveTo(0, rect.height / 2);
          points.forEach((point, index) => {
            if (index === 0) {
              ctx.moveTo(point.x, point.y);
            } else {
              const prevPoint = points[index - 1];
              const cpX = (prevPoint.x + point.x) / 2;
              const cpY = (prevPoint.y + point.y) / 2;
              ctx.quadraticCurveTo(prevPoint.x, prevPoint.y, cpX, cpY);
            }
          });
          ctx.lineTo(rect.width, rect.height / 2);
          ctx.stroke();

          // Draw waveform with history
          historyRef.current.forEach((data, historyIndex) => {
            const alpha = (historyIndex + 1) / historyRef.current.length;

            // Create gradient for the waveform - using a professional blue color
            const gradient = ctx.createLinearGradient(0, 0, 0, rect.height);
            gradient.addColorStop(0, `rgba(10, 132, 255, ${alpha * 0.4})`);
            gradient.addColorStop(1, `rgba(10, 132, 255, ${alpha * 0.4})`);

            ctx.strokeStyle = gradient;
            ctx.lineWidth = 2;

            // Draw waveform
            ctx.beginPath();
            const sliceWidth = rect.width / (data.length - 1);

            // Draw top half
            for (let i = 0; i < data.length; i++) {
              const x = i * sliceWidth;
              const amplitude = data[i] * rect.height * 0.25;
              const y = rect.height / 2 - amplitude;

              if (i === 0) {
                ctx.moveTo(x, y);
              } else {
                const prevX = (i - 1) * sliceWidth;
                const prevAmplitude = data[i - 1] * rect.height * 0.25;
                const prevY = rect.height / 2 - prevAmplitude;
                const cpX = (prevX + x) / 2;
                ctx.quadraticCurveTo(prevX, prevY, cpX, (prevY + y) / 2);
              }
            }

            // Draw bottom half (mirror)
            for (let i = data.length - 1; i >= 0; i--) {
              const x = i * sliceWidth;
              const amplitude = data[i] * rect.height * 0.25;
              const y = rect.height / 2 + amplitude;

              if (i === data.length - 1) {
                ctx.lineTo(x, y);
              } else {
                const prevX = (i + 1) * sliceWidth;
                const prevAmplitude = data[i + 1] * rect.height * 0.25;
                const prevY = rect.height / 2 + amplitude;
                const cpX = (prevX + x) / 2;
                ctx.quadraticCurveTo(prevX, prevY, cpX, (prevY + y) / 2);
              }
            }

            ctx.closePath();
            ctx.stroke();
          });

          // Draw moving grid lines during recording
          ctx.strokeStyle = "rgba(0, 0, 0, 0.05)";
          ctx.lineWidth = 1;
          for (let i = -30; i < rect.width + 30; i += 30) {
            const x = (i - offsetRef.current + rect.width) % rect.width;
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, rect.height);
            ctx.stroke();
          }
        } else {
          // Draw static line in the middle
          ctx.beginPath();
          ctx.strokeStyle = "rgba(10, 132, 255, 0.3)";
          ctx.lineWidth = 2;
          ctx.moveTo(0, rect.height / 2);
          ctx.lineTo(rect.width, rect.height / 2);
          ctx.stroke();

          // Draw static grid lines
          ctx.strokeStyle = "rgba(0, 0, 0, 0.05)";
          ctx.lineWidth = 1;
          for (let i = 0; i < rect.width; i += 30) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, rect.height);
            ctx.stroke();
          }
        }
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording, isPaused, frequencyData]);

  // Force update hook
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#1D1D1F]/95 backdrop-blur-xl z-[9999]">
      <div className="h-full flex flex-col items-center justify-center relative">
        {/* Logo positioned at top left */}
        <div className="absolute top-8 left-8 z-50">
          <img src={logoImage} alt="DocuMD" className="h-16" />
        </div>

        {permissionError ? (
          <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 rounded-full bg-[#FF453A]/10 flex items-center justify-center mx-auto">
                <Mic className="w-8 h-8 text-[#FF453A]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-white mb-4">
              Microphone Access Required
            </h3>
            {isSafari ? (
              <>
                <p className="text-white/80 mb-6">
                  Safari has blocked microphone access for this website. Follow
                  these steps to enable it:
                </p>
                <ol className="text-white/80 mb-8 text-left list-decimal pl-6 space-y-3">
                  <li className="leading-relaxed">
                    Click Safari in the top menu bar
                  </li>
                  <li className="leading-relaxed">
                    Select "Settings for This Website" (or press ⌘+I)
                  </li>
                  <li className="leading-relaxed">
                    Find "Microphone" in the popup menu
                  </li>
                  <li className="leading-relaxed">
                    Change it from "Deny" to "Allow"
                  </li>
                  <li className="leading-relaxed">Click "Try Again" below</li>
                </ol>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      window.location.reload();
                    }}
                    className="w-full px-6 py-3 bg-[#0A84FF] text-white rounded-full font-medium hover:bg-[#0A84FF]/90 transition-all active:scale-95"
                  >
                    Refresh Page
                  </button>
                  <button
                    type="button"
                    onClick={retryRecording}
                    className="w-full px-6 py-3 bg-[#32D74B] text-white rounded-full font-medium hover:bg-[#32D74B]/90 transition-all active:scale-95"
                  >
                    Try Again
                  </button>
                  <button
                    type="button"
                    onClick={handleNewRecording}
                    className="w-full px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-all active:scale-95"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : isChrome ? (
              <>
                <p className="text-white/80 mb-6">
                  Chrome has blocked microphone access. Look for the camera icon
                  in your address bar:
                </p>
                <div className="fixed top-0 right-16 z-[10000]">
                  <div className="relative">
                    <div className="absolute -top-1 right-0 flex flex-col items-center animate-bounce">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <path d="M20 0L40 20H0L20 0Z" fill="#FF453A" />
                      </svg>
                      <div className="text-white text-sm mt-1 whitespace-nowrap bg-[#FF453A] px-3 py-1.5 rounded-full">
                        Click here
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-12">
                  <ol className="text-white/80 mb-8 text-left list-decimal pl-6 space-y-3">
                    <li className="leading-relaxed">
                      Click the camera/microphone icon in the address bar
                    </li>
                    <li className="leading-relaxed">
                      Select "Allow" for microphone access
                    </li>
                    <li className="leading-relaxed">Click "Try Again" below</li>
                  </ol>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={retryRecording}
                      className="w-full px-6 py-3 bg-[#0A84FF] text-white rounded-full font-medium hover:bg-[#0A84FF]/90 transition-all active:scale-95"
                    >
                      Try Again
                    </button>
                    <button
                      type="button"
                      onClick={handleNewRecording}
                      className="w-full px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-all active:scale-95"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-white/80 mb-8">
                  Please allow microphone access in your browser settings to
                  start recording. You can try requesting permission again or
                  close and try later.
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={retryRecording}
                    className="w-full px-6 py-3 bg-[#0A84FF] text-white rounded-full font-medium hover:bg-[#0A84FF]/90 transition-all active:scale-95"
                  >
                    Request Permission
                  </button>
                  <button
                    type="button"
                    onClick={handleNewRecording}
                    className="w-full px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-all active:scale-95"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Recording animation container - removed green flashing effects */}
            <div className="absolute inset-0 w-full h-full bg-white">
              <canvas
                ref={canvasRef}
                className="w-full h-full"
                style={{ width: "100%", height: "100%" }}
              />
            </div>

            {/* Content overlay */}
            <div className="relative z-10 w-full max-w-2xl mx-auto px-6 py-12 flex flex-col items-center">
              {/* Timer */}
              <div className="mb-12 text-center">
                <div className="text-[120px] font-sf-mono text-[#1D1D1F] font-light select-none tracking-tight">
                  {formatTime(timer)}
                </div>
              </div>

              {/* Status with Animation - updated colors */}
              <div className="mb-12 flex items-center justify-center gap-3 select-none">
                <div
                  className={`w-2.5 h-2.5 rounded-full ${
                    isRecording
                      ? isPaused
                        ? "bg-[#FFB340]"
                        : "bg-[#0A84FF] animate-pulse"
                      : isSending
                      ? "bg-[#0A84FF] animate-pulse"
                      : isPlaying
                      ? "bg-[#0A84FF] animate-pulse"
                      : "bg-[#0A84FF]"
                  }`}
                />
                <span className="text-lg text-[#1D1D1F] font-medium tracking-tight">
                  {isRecording
                    ? isPaused
                      ? "Recording Paused"
                      : "Recording..."
                    : isSending
                    ? "Uploading..."
                    : isPlaying
                    ? "Playing..."
                    : "Ready"}
                </span>
                {/* Add bouncing dots for recording state */}
                {isRecording && !isPaused && (
                  <div className="flex gap-1 ml-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-[#32D74B]"
                        style={{
                          animation: "bounce 1.4s infinite ease-in-out both",
                          animationDelay: `${i * 0.16}s`,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Controls with Animation */}
              <div className="flex items-center justify-center gap-6 select-none">
                {isRecording ? (
                  <>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handlePauseResume();
                      }}
                      className={`w-20 h-20 rounded-full relative ${
                        isPaused
                          ? "bg-[#FFB340] hover:bg-[#FFB340]/90"
                          : "bg-[#1D1D1F] hover:bg-[#1D1D1F]/90"
                      } flex items-center justify-center transition-all active:scale-95 shadow-lg`}
                    >
                      {isPaused ? (
                        <Play className="w-10 h-10 text-white" />
                      ) : (
                        <Pause className="w-10 h-10 text-white" />
                      )}
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        stopRecording();
                      }}
                      className="w-20 h-20 rounded-full bg-[#FF453A] hover:bg-[#FF453A]/90 flex items-center justify-center transition-all active:scale-95 relative"
                      disabled={isSending}
                    >
                      {/* Add pulsing effect for recording button */}
                      {!isPaused && (
                        <>
                          <div className="absolute inset-0 rounded-full bg-[#FF453A]/20 animate-ping" />
                          <div className="absolute inset-0 rounded-full bg-[#FF453A]/10 animate-pulse" />
                        </>
                      )}
                      <StopCircle className="w-10 h-10 text-white relative z-10" />
                    </button>
                  </>
                ) : isSending ? (
                  <div className="fixed inset-0 bg-white/95 backdrop-blur-xl z-20 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-8 max-w-md mx-auto px-6 text-center">
                      {/* Improved loader animation */}
                      <div className="relative">
                        <div className="w-24 h-24 rounded-full border-4 border-[#0A84FF]/20 animate-[spin_3s_linear_infinite]" />
                        <div className="w-24 h-24 rounded-full border-4 border-t-[#0A84FF] absolute inset-0 animate-[spin_1.5s_linear_infinite]" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg
                            className="w-10 h-10 text-[#0A84FF] animate-pulse"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-semibold text-[#1D1D1F]">
                          Uploading Recording
                        </h3>
                        <p className="text-[#86868b] text-sm max-w-[300px] leading-relaxed">
                          Please keep this window open while we process your
                          recording. This may take a few moments.
                        </p>
                        <div className="flex items-center justify-center gap-2 text-[#0A84FF] font-medium">
                          <div className="w-2 h-2 rounded-full bg-[#0A84FF] animate-[bounce_1s_infinite]" />
                          <div className="w-2 h-2 rounded-full bg-[#0A84FF] animate-[bounce_1s_infinite_0.2s]" />
                          <div className="w-2 h-2 rounded-full bg-[#0A84FF] animate-[bounce_1s_infinite_0.4s]" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : audioBlob && !isPlaying ? (
                  <div className="flex items-center gap-6">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handlePlayback();
                      }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="w-20 h-20 rounded-full bg-[#32D74B] hover:bg-[#32D74B]/90 flex items-center justify-center transition-all active:scale-95">
                        <Play className="w-10 h-10 text-white" />
                      </div>
                      <span className="text-white/90 font-medium text-sm">
                        Play
                      </span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        processAudio(audioBlob);
                      }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="w-20 h-20 rounded-full bg-[#0A84FF] hover:bg-[#0A84FF]/90 flex items-center justify-center transition-all active:scale-95">
                        <svg
                          className="w-10 h-10 text-white"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <span className="text-white/90 font-medium text-sm">
                        Upload
                      </span>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handlePlayback();
                    }}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-20 h-20 rounded-full bg-[#0A84FF] hover:bg-[#0A84FF]/90 flex items-center justify-center transition-all active:scale-95">
                      {isPlaying ? (
                        <Pause className="w-10 h-10 text-white" />
                      ) : (
                        <Play className="w-10 h-10 text-white" />
                      )}
                    </div>
                    <span className="text-white/90 font-medium text-sm">
                      {isPlaying ? "Pause" : "Play"}
                    </span>
                  </button>
                )}
              </div>

              {/* Close Button - Updated styles */}
              {!isRecording && !isSending && (
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (audioBlob && !isPlaying) {
                      if (
                        window.confirm(
                          "Are you sure you want to close? The recording will be lost if not uploaded."
                        )
                      ) {
                        handleNewRecording();
                      }
                    } else {
                      handleNewRecording();
                    }
                  }}
                  className="absolute top-6 right-6 w-12 h-12 rounded-full bg-[#1D1D1F] hover:bg-[#1D1D1F]/90 flex items-center justify-center transition-all active:scale-95 shadow-lg"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecordingModal;
